import { css } from "@emotion/css";
import type { SimpleMenuItem, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { useMenuState, SimpleMenu, ActionButtonType, ActionButton, ListItemLink, List, ListItemContent } from "@octopusdeploy/design-system-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { AccountResource, AccountType, EnvironmentResource, TenantResource, Repository } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { links } from "@octopusdeploy/portal-routes";
import { sortBy, uniq } from "lodash";
import * as React from "react";
import { createAddAccountAnalyticsEvent } from "~/areas/infrastructure/components/AccountEdit/amplitudeAnalytics";
import { DropDownIcon } from "~/components/Button/DropDownIcon/DropDownIcon";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import MarkdownDescription from "~/components/MarkdownDescription";
import ExternalLink from "~/components/Navigation/ExternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle/ListTitle";
import SectionHeading from "~/primitiveComponents/dataDisplay/Section/SectionHeading";
import { environmentChipListIncludingMissing, tenantChipListIncludingMissing } from "../../../../components/Chips/index";
import { RawAccountTypeDetailsMap } from "../../InfrastructureDetails";
import styles from "./style.module.less";
interface AccountsPageProps {
    spaceId: string;
    loaderData: AccountsPageLoaderData;
}
interface AccountsPageLoaderData {
    accounts: AccountResource[];
    environments: EnvironmentResource[];
    tenants: TenantResource[];
}
export async function accountsPageLoader(repository: Repository): Promise<AccountsPageLoaderData> {
    const accounts = await repository.Accounts.all();
    const environmentIds = uniq(accounts.reduce<string[]>((list, acc) => list.concat(acc.EnvironmentIds), []));
    const tenantIds = uniq(accounts.reduce<string[]>((list, acc) => list.concat(acc.TenantIds), []));
    const tenantsPromise = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all({ ids: tenantIds }) : Promise.resolve([]);
    const environments = await repository.Environments.all({ ids: environmentIds });
    return { accounts: accounts, environments: environments, tenants: await tenantsPromise };
}
export function AccountsPage({ spaceId, loaderData }: AccountsPageProps) {
    const Title = "Accounts";
    const { accounts, environments, tenants } = loaderData;
    const addAccountPageAction: PrimaryPageAction = {
        type: "custom",
        key: "Add Account",
        content: <AddAccountButton spaceId={spaceId}/>,
        hasPermissions: isAllowed({
            permission: Permission.AccountCreate,
            wildcard: true,
        }),
    };
    if (accounts.length === 0) {
        return (<PortalEmptyStateOnboarding title={"Add accounts to use during your deployments"} description={"Accounts help you to centralise account details used during your deployments, including things like username/password, tokens, Azure and AWS credentials and SSH key pairs."} primaryAction={addAccountPageAction} learnMore={<ExternalLink href="OnboardingAccountsLearnMore">Learn more</ExternalLink>}/>);
    }
    return (<PageContent header={{ title: Title, primaryAction: addAccountPageAction }}>
            <BorderedContent>
                {RawAccountTypeDetailsMap.map((accountTypeDetail) => (<AccountGroup key={accountTypeDetail.name} spaceId={spaceId} accounts={accounts} environments={environments} tenants={tenants} accountTypes={accountTypeDetail.types} groupName={accountTypeDetail.namePlural}/>))}
            </BorderedContent>
        </PageContent>);
}
interface AccountGroupProps {
    spaceId: string;
    accounts: AccountResource[];
    environments: EnvironmentResource[];
    tenants: TenantResource[];
    accountTypes: AccountType[];
    groupName: string;
}
function AccountGroup({ spaceId, accounts, environments, tenants, accountTypes, groupName }: AccountGroupProps) {
    const filteredAccounts = accounts.filter((account) => accountTypes.indexOf(account.AccountType) !== -1);
    if (!filteredAccounts || filteredAccounts.length === 0) {
        return null;
    }
    const sortedAccounts = sortBy(filteredAccounts, (account) => account.Name);
    const title = (<div className={styles.typeHeader}>
            <div className={styles.typeHeaderTitleContainer}>{groupName}</div>
        </div>);
    return (<>
            <SectionHeading title={title}/>
            <List<AccountResource> items={sortedAccounts} renderRow={({ item: account }) => <AccountsListItem spaceId={spaceId} account={account} environments={environments} tenants={tenants}/>}/>
        </>);
}
function AccountsListItem({ spaceId, account, environments, tenants }: {
    spaceId: string;
    account: AccountResource;
    environments: EnvironmentResource[];
    tenants: TenantResource[];
}) {
    return (<ListItemLink href={links.editInfrastructureAccountPage.generateUrl({ spaceId, accountId: account.Id })}>
            <ListItemContent>
                <ListTitle>{account.Name}</ListTitle>
                {environmentChipListIncludingMissing(environments, account.EnvironmentIds)}
                {tenantChipListIncludingMissing(tenants, account.TenantIds)}
                <MarkdownDescription markup={account.Description}/>
            </ListItemContent>
        </ListItemLink>);
}
function AddAccountButton({ spaceId }: {
    spaceId: string;
}) {
    const [openMenu, menuState, buttonArriaAttributes] = useMenuState();
    const trackEvent = useTrackEvent();
    let rawAccountTypeDetailsMap = RawAccountTypeDetailsMap;
    if (!isFeatureToggleEnabled("GenericOidcAccountsFeatureToggle")) {
        rawAccountTypeDetailsMap = rawAccountTypeDetailsMap.filter((detail) => detail.name != "Generic OIDC");
    }
    const addAccountItems: SimpleMenuItem[] = rawAccountTypeDetailsMap.map((accountTypeDetails) => ({
        type: "internal-link",
        label: accountTypeDetails.name,
        path: links.createInfrastructureAccountPage.generateUrl({ spaceId }, { accountType: accountTypeDetails.types[0] }),
        onClick: () => {
            trackEvent(createAddAccountAnalyticsEvent({ "Account Type": accountTypeDetails.types[0], Location: "Page" }));
        },
    }));
    return (<>
            <ActionButton type={ActionButtonType.Primary} icon={<DropDownIcon />} iconPosition="right" label="Add Account" onClick={openMenu} menuButtonAttributes={buttonArriaAttributes}/>
            <SimpleMenu menuState={menuState} items={addAccountItems} accessibleName={"Add account"} compact={true}/>
        </>);
}
// Custom bordered content so that we dont get double borders with the form headers at the top
export function BorderedContent({ children }: {
    children: React.ReactNode;
}) {
    return <div className={borderedContentStyles}>{children}</div>;
}
const borderedContentStyles = css({
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderTop: "none",
    borderRadius: borderRadius.medium,
    overflow: "clip",
});
